import React, { useEffect, useState } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import AiModelModal from "../AiModelModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginTop: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
}));

const AiModelsManager = () => {
  const classes = useStyles();

  const getCategoryDescription = (cat) => {
    switch (cat.toLowerCase()) {
      case "chat": return "(Agente de IA / Chatbot WhatsApp)";
      case "text generation": return "(Herramientas Web / Mejorador de Prompts)";
      case "text to image": return "(Generación de Imágenes)";
      case "image to image": return "(Edición y Modificación de Imágenes)";
      case "text to video": return "(Generación de Videos)";
      case "audio": return "(Locución y Clonación de Voz)";
      case "musica": return "(Creación de Música IA)";
      case "voz": return "(Generador de Voces Básico / TTS)";
      case "embedding": return "(Modelos Vectoriales / Base de Conocimiento RAG)";
      default: return "";
    }
  };

  const [aiModels, setAiModels] = useState([]);
  const [loading, setLoading] = useState(false);

  const [aiModelModalOpen, setAiModelModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingModel, setDeletingModel] = useState(null);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-models");
      setAiModels(data);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleOpenAiModelModal = () => {
    setSelectedModel(null);
    setAiModelModalOpen(true);
  };

  const handleCloseAiModelModal = () => {
    setSelectedModel(null);
    setAiModelModalOpen(false);
  };

  const handleEditAiModel = (aiModel) => {
    setSelectedModel(aiModel);
    setAiModelModalOpen(true);
  };

  const handleDeleteAiModel = async (aiModelId) => {
    try {
      await api.delete(`/ai-models/${aiModelId}`);
      toast.success(i18n.t("aiModels.deleteModelSuccess"));
      setAiModels(aiModels.filter((m) => m.id !== aiModelId));
    } catch (err) {
      toastError(err);
    }
    setDeletingModel(null);
  };

  return (
    <>
      <ConfirmationModal
        title={
          deletingModel &&
          i18n.t("aiModels.confirmDeleteModel", { name: deletingModel.name })
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteAiModel(deletingModel.id)}
      >
        {i18n.t("aiModels.deleteModelWarning")}
      </ConfirmationModal>
      <AiModelModal
        open={aiModelModalOpen}
        onClose={handleCloseAiModelModal}
        aiModelId={selectedModel?.id}
        reloadModels={fetchModels}
      />

      <div className={classes.header}>
        <Title>{i18n.t("aiModels.title")}</Title>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenAiModelModal}
        >
          {i18n.t("aiModels.addModel")}
        </Button>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("aiModels.colName")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colCategory")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colApiId")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colCostBase")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colSaleImage")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colSaleInput")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colSaleOutput")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colResolution")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colQuality")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colProMode")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colOfficialPrice")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colProfit")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colStatus")}</TableCell>
              <TableCell align="center">{i18n.t("aiModels.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {Object.keys(aiModels.reduce((acc, model) => {
                const category = model.type || "Otros";
                if (!acc[category]) acc[category] = [];
                acc[category].push(model);
                return acc;
              }, {})).sort().map(category => (
                <React.Fragment key={category}>
                  <TableRow style={{ backgroundColor: "rgba(128, 128, 128, 0.1)" }}>
                    <TableCell colSpan={14} align="left" style={{ fontWeight: "bold", fontSize: "1.1em", paddingLeft: "16px", color: "#00bfff" }}>
                      {category.toUpperCase()}
                      <span style={{ fontSize: "0.85em", color: "#e0e0e0", fontWeight: "normal", marginLeft: "10px" }}>
                        {getCategoryDescription(category)}
                      </span>
                    </TableCell>
                  </TableRow>
                  {aiModels.filter(m => (m.type || "Otros") === category).map((model) => (
                <TableRow key={model.id}>
                  <TableCell align="center">{model.name}</TableCell>
                  <TableCell align="center">{model.type}</TableCell>
                  <TableCell align="center">{model.apiIdentifier}</TableCell>
                  <TableCell align="center">${Number(model.costPrice).toFixed(4)}</TableCell>
                  <TableCell align="center">${Number(model.credits).toFixed(4)}</TableCell>
                  <TableCell align="center">{model.salePricePrompt ? `$${Number(model.salePricePrompt).toFixed(6)}` : "-"}</TableCell>
                  <TableCell align="center">{model.salePriceCompletion ? `$${Number(model.salePriceCompletion).toFixed(6)}` : "-"}</TableCell>
                  <TableCell align="center">{model.apiParameters?.resolution || "-"}</TableCell>
                  <TableCell align="center">{model.apiParameters?.quality || "-"}</TableCell>
                  <TableCell align="center">{model.apiParameters?.enable_pro ? "PRO" : "-"}</TableCell>
                  <TableCell align="center">${Number(model.officialPrice || 0).toFixed(4)}</TableCell>
                  <TableCell align="center">
                    {(() => {
                      const cost = Number(model.costPrice);
                      const sale = Number(model.credits);
                      if (!cost || cost <= 0) return "-";
                      const pct = Math.round(((sale - cost) / cost) * 100);
                      if (pct > 0) {
                        return (
                          <span style={{ color: '#4caf50', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                            ▲ {pct}%
                          </span>
                        );
                      } else if (pct < 0) {
                        return (
                          <span style={{ color: '#f44336', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                            ▼ -{Math.abs(pct)}%
                          </span>
                        );
                      } else {
                        return <span style={{ color: '#9e9e9e' }}>0%</span>;
                      }
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {model.active ? i18n.t("aiModels.statusActive") : i18n.t("aiModels.statusInactive")}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditAiModel(model)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingModel(model);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
                  ))}
                </React.Fragment>
              ))}
              {loading && <TableRowSkeleton columns={14} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};

export default AiModelsManager;
